import { CafClusterListItem, CafCorpusEntry } from '../../../api/cafCorpus';
import { LenderRule, lenderFor, senderEmail } from '../lenders';

export const NO_LENDER = 'No lender set';
export type LenderOf = (sender: string) => string | null;

export const lenderName = (sender: string, lenderOf: LenderOf) => lenderOf(sender) ?? NO_LENDER;

const haystack = (parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' ').toLowerCase();

const inLender = (sender: string, lender: string, lenderOf: LenderOf) => lender === 'all' || lenderName(sender, lenderOf) === lender;

export const groupMatches = (group: CafClusterListItem, query: string, lender: string, lenderOf: LenderOf) => {
  const q = query.trim().toLowerCase();
  return inLender(group.sender, lender, lenderOf)
    && (!q || haystack([lenderOf(group.sender), senderEmail(group.sender), ...(group.sample_subjects ?? [])]).includes(q));
};

/** Spec §4.3: an approved example that does not yet say whether its emails are final responses. */
export const needsFinalResponseSetting = (entry: CafCorpusEntry) =>
  entry.final_response_stance !== 'final_response' && entry.final_response_stance !== 'not_final_response';

export const exampleMatches = (entry: CafCorpusEntry, query: string, lender: string, lenderOf: LenderOf,
  onlyNeedsSetting = false) => {
  const q = query.trim().toLowerCase();
  return inLender(entry.sender, lender, lenderOf)
    && (!onlyNeedsSetting || needsFinalResponseSetting(entry))
    && (!q || haystack([lenderOf(entry.sender), senderEmail(entry.sender), entry.note, entry.subject_snapshot]).includes(q));
};

export const lenderOptions = (senders: string[], lenderOf: LenderOf): string[] => {
  const names = Array.from(new Set(senders.map((sender) => lenderName(sender, lenderOf))));
  const named = names.filter((name) => name !== NO_LENDER).sort((a, b) => a.localeCompare(b));
  return names.indexOf(NO_LENDER) >= 0 ? [...named, NO_LENDER] : named;
};

/** Resolves every sender's lender ONCE (not once per row per render): build this whenever the visible
 *  senders or the lender rules change, then have `lenderOf` read from it. */
export const buildLenderMap = (senders: string[], rules: LenderRule[]): Map<string, string | null> => {
  const map = new Map<string, string | null>();
  senders.forEach((sender) => {
    if (!map.has(sender)) map.set(sender, lenderFor(sender, rules));
  });
  return map;
};
